import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import BackToTop from '../../components/feature/BackToTop';
import LoadingScreen from '../../components/feature/LoadingScreen';

export default function About() {
  const [institutions, setInstitutions] = useState(0);
  const [funds, setFunds] = useState(0);
  const [successRate, setSuccessRate] = useState(0);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Add Schema.org JSON-LD for About Page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About ProEditions - Professional Grant Writing Services",
      "description": "Since 2016, ProEditions has been the trusted partner for businesses and nonprofits seeking expert writing and development services. 100% success rate with over $7M+ in secured funding.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "ProEditions",
        "foundingDate": "2016",
        "description": "ProEditions specializes in grant writing and application development, boasting a 100% success rate and securing over $7 million in funding for clients.",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "logo": "https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/23d62a2a9bfd7e2273fccca497545bc9.png",
        "email": "chanelle@proeditionssc.com",
        "numberOfEmployees": {
          "@type": "QuantitativeValue",
          "value": "12+"
        }
      }
    });
    document.head.appendChild(script);

    // Start animations after a small delay
    setTimeout(() => {
      // Animate funds counter
      const fundsInterval = setInterval(() => {
        setFunds(prev => {
          if (prev >= 7) {
            clearInterval(fundsInterval);
            return 7;
          }
          return prev + 0.5;
        });
      }, 80);

      // Animate institutions counter
      const institutionsInterval = setInterval(() => {
        setInstitutions(prev => {
          if (prev >= 12) {
            clearInterval(institutionsInterval);
            return 12;
          }
          return prev + 1;
        });
      }, 70);

      // Animate success rate counter
      const successInterval = setInterval(() => {
        setSuccessRate(prev => {
          if (prev >= 100) {
            clearInterval(successInterval);
            return 100;
          }
          return prev + 5;
        });
      }, 25);

      return () => {
        clearInterval(fundsInterval);
        clearInterval(institutionsInterval);
        clearInterval(successInterval);
      };
    }, 300);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const openCalendly = () => {
    window.open('https://calendly.com/chanelle-proeditionssc/30min', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <LoadingScreen />
      <Header />
      
      <main className="pt-32 pb-16">
        {/* Hero Section - Two Column Layout */}
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-16 items-start">
              {/* Left Column - Text Content */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-5xl md:text-6xl font-light mb-4 text-gray-900">
                    About <span className="font-bold text-[#1a4d2e]">ProEditions</span>
                  </h1>
                  <p className="text-xl text-gray-600 mb-6">
                    Empowering success since 2016
                  </p>
                </div>

                <div>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    At ProEditions, we are dedicated to empowering entrepreneurs, small businesses, and non-profit organizations through expert business writing services. Whether you're launching a new venture or scaling an established organization, our tailored services are designed to help you secure funding, develop compelling applications, and communicate your vision effectively.
                  </p>
                  
                  <button 
                    onClick={openCalendly}
                    className="bg-[#1a4d2e] text-white px-10 py-4 text-lg font-semibold hover:bg-[#2d7a4a] transition-colors whitespace-nowrap cursor-pointer rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    aria-label="Schedule a free consultation with ProEditions"
                  >
                    Schedule a Free Consultation
                  </button>
                </div>
              </div>

              {/* Right Column - Logo and Animated Stats */}
              <div className="space-y-8">
                <div className="text-center">
                  <img 
                    src="https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/23d62a2a9bfd7e2273fccca497545bc9.png" 
                    alt="ProEditions Logo - Professional Grant Writing Services" 
                    title="ProEditions - Expert Grant Writing"
                    className="w-64 mx-auto mb-8"
                    loading="eager"
                  />
                </div>

                {/* Animated Stats - Single Row */}
                <div className="grid grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold mb-3 text-orange-500">
                      ${funds > 0 ? Math.round(funds) : 0}M+
                    </div>
                    <div className="text-base text-gray-600 font-medium">
                      Secured Funds
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold mb-3 text-[#1a4d2e]">
                      {institutions}+
                    </div>
                    <div className="text-base text-gray-600 font-medium">
                      Organizations & Institutions Launched
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-4xl md:text-5xl font-bold mb-3 text-orange-500">
                      {successRate}%
                    </div>
                    <div className="text-base text-gray-600 font-medium">
                      Success Rate
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Expertise - Left Aligned with Accent Border */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-1">
                <div className="w-1 h-full bg-gradient-to-b from-orange-500 to-amber-500 rounded-full hidden md:block"></div>
              </div>
              <div className="md:col-span-11">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-2xl text-white" aria-hidden="true"></i>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-light text-gray-900">
                    Our <span className="font-bold text-orange-500">Expertise</span>
                  </h2>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed max-w-5xl">
                  With years of experience in grant writing, application development, and business communications, we have helped our clients secure over $7 million in funding. Our team combines strategic thinking with exceptional writing skills to deliver results that exceed expectations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach - Right Aligned */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-11 order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6 justify-start md:justify-end">
                  <h2 className="text-4xl md:text-5xl font-light text-gray-900 text-left md:text-right">
                    Our <span className="font-bold text-[#1a4d2e]">Approach</span>
                  </h2>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#1a4d2e] to-[#2d7a4a] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-compass-line text-2xl text-white" aria-hidden="true"></i>
                  </div>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed max-w-5xl text-left md:text-right md:ml-auto">
                  We believe in a personalized approach to every project. We take the time to understand your unique needs, goals, and challenges. Our collaborative process ensures that every document we create authentically represents your organization and resonates with your target audience.
                </p>
              </div>
              <div className="md:col-span-1 order-1 md:order-2">
                <div className="w-1 h-full bg-gradient-to-b from-[#1a4d2e] to-[#2d7a4a] rounded-full hidden md:block ml-auto"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Commitment - Left Aligned */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-1">
                <div className="w-1 h-full bg-gradient-to-b from-orange-500 to-amber-500 rounded-full hidden md:block"></div>
              </div>
              <div className="md:col-span-11">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-white" aria-hidden="true"></i>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-light text-gray-900">
                    Our <span className="font-bold text-orange-500">Commitment</span>
                  </h2>
                </div>
                <p className="text-xl text-gray-700 leading-relaxed max-w-5xl">
                  We are committed to delivering high-quality work on time, every time. Our attention to detail, strategic approach, and dedication to your success set us apart. When you work with ProEditions, you gain a partner who is invested in your growth and success.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose ProEditions - Timeline Style */}
        <section className="py-20 bg-white pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">
                Why Choose <span className="font-bold text-[#1a4d2e]">ProEditions</span>
              </h2>
              <p className="text-xl text-gray-600">
                Your success is our priority
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1a4d2e] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  1
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Proven Track Record</h3>
                  <p className="text-gray-700">100% success rate with over $7 million secured in funding for our clients</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  2
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Personalized Service</h3>
                  <p className="text-gray-700">Every project receives individual attention and customized solutions</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1a4d2e] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  3
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Team</h3>
                  <p className="text-gray-700">Experienced professionals with deep knowledge in grant writing and business development</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  4
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">On-Time Delivery</h3>
                  <p className="text-gray-700">We respect your deadlines and deliver quality work when you need it</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <BackToTop />
    </div>
  );
}
